import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Tak for din henvendelse! Vi kontakter dig snarest.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Der opstod en fejl. Prøv venligst igen.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Telefon",
      value: "+45 6136 2199",
      href: "tel:+4561362199",
    },
    {
      icon: Mail,
      label: "Email",
      value: "info@salgsrenovering.dk",
      href: "mailto:info@salgsrenovering.dk",
    },
    {
      icon: MapPin,
      label: "Adresse",
      value: "Hørsholm, Danmark",
      href: "#",
    },
  ];

  return (
    <section 
      id="kontakt" 
      ref={ref}
      className="py-24 md:py-32"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
              Kontakt Os
            </span>
            <h2 
              className="mt-4 text-4xl md:text-5xl font-bold text-foreground tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Lad os tale om
              <br />
              dit projekt
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Kontakt os for en uforpligtende snak om dine muligheder. 
              Vi giver gerne en vurdering af, hvordan vi kan hjælpe dig 
              med at maksimere din boligs værdi.
            </p>

            {/* Contact Info Cards */}
            <div className="mt-10 space-y-6">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={index}
                  href={item.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-secondary/50 hover:bg-secondary transition-colors group"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="w-12 h-12 bg-primary flex items-center justify-center group-hover:bg-accent transition-colors">
                    <item.icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">{item.label}</div>
                    <div className="font-medium text-foreground">{item.value}</div>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 md:p-10 border border-border"
          >
            <h3 
              className="text-2xl font-bold text-foreground mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Send os en besked
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-sm font-medium">
                  Navn *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2 rounded-none"
                  placeholder="Dit fulde navn"
                  data-testid="contact-name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none"
                    placeholder="din@email.dk"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Telefon *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none"
                    placeholder="+45 12 34 56 78"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm font-medium">
                  Besked *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="mt-2 rounded-none resize-none"
                  placeholder="Fortæl os om dit projekt..."
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none py-6"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  "Sender..."
                ) : (
                  <>
                    Send Besked
                    <Send className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
